import {
  AbsoluteCenter,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Button,
  Input,
  useToast,
  Box,
} from '@chakra-ui/react';
import { Link as Chakralink } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { Spinner } from '@chakra-ui/react';
import moment from 'moment';
import React, { useState, useEffect, useRef } from 'react';
import { AddIcon, ViewIcon } from '@chakra-ui/icons';
// Custom components
import TableWrapper from 'components/tableWrapper/tableWrapper';
import PermissionGroupApi from 'api/permissiongroup';
import { useSelectedPermissions } from 'contexts/PermissionGroupArrayContext';
import { useHistory } from 'react-router-dom';
import PageError from 'components/nopermission/PageError';
import { usePermissions } from 'contexts/PermissionContext';
import { Modules } from 'config/constant';
import NoPermissionsMessage from 'components/nopermission/NoPermissions';

export default function TableData(props) {
  const { resetSelectedPermissions } = useSelectedPermissions();
  const history = useHistory();
  const { secondary } = props;
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const [isError, setIsError] = useState('');
  const [list, setList] = useState([]);
  const [name, setName] = useState();
  const [displayOrder, setDisplayOrder] = useState();
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [listTotal, setListTotal] = useState(2);
  const [searchTerm, setSearchTerm] = useState('');
  const [booleanFilter, setBooleanFilter] = useState(false);
  const [fetchListError, setFetchListError] = useState(false);

  const { getPermissionsForModule } = usePermissions();
  const [permissions, setPermissions] = useState(null);

  const debounceTimeout = useRef(null); // Reference to track debounce timeout
  const handleInputChange = (value) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      handleSearch(value); // Call the actual search handler after debounce time
    }, 800); // Debounce delay (e.g., 800ms)
  };
  const handleSearch = async (search) => {
    setSearchTerm(search);
  };

  useEffect(() => {
    const permissions = getPermissionsForModule([Modules.PermissionGroup]);
    setPermissions(permissions);
  }, []);

  const fetchList = async () => {
    try {
      setLoading(true);
      let response = await PermissionGroupApi.List({
        currentPage,
        itemsPerPage,
        searchTerm,
        booleanFilter,
      });
      response = response.data;
      setLoading(false);
      if (response.status_code === 200) {
        setFetchListError(false);
        setList(response?.data?.list);
        setListTotal(response?.data?.total);
      } else {
        setFetchListError(true);
        setList(null);
        setListTotal(0);
      }
    } catch (err) {
      setFetchListError(true);
      console.log(err);
    }
  };
  useEffect(() => {
    if (permissions?.read) {
      fetchList();
    }
  }, [permissions, currentPage, searchTerm, booleanFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, booleanFilter]);

  const handleDelete = async (e) => {
    try {
      const id = [e.target.value];
      const response = await PermissionGroupApi.Delete({ id });
      if (response.data.statusCode === 1) {
        toast({
          title: 'Successfully Deleted',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      } else if (response.data.statusCode !== 1) {
        let error = response.data.error.errorMessage;
        toast({
          title: error,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
      fetchList();
    } catch (err) {
      console.log(err);
    }
  };
  const handleUpdate = async (onClose, id) => {
    try {
      let request = {
        id: id,
        name: name,
        display_order: Number(displayOrder),
      };
      const response = await PermissionGroupApi.Update(request);
      if (response.data.status_code == 200) {
        toast({
          title: response?.data?.message || 'Successfully Updated',
          status: 'success',
          duration: 1000,
          isClosable: true,
          position: 'top',
        });
        onClose();
        fetchList();
      } else if (response.data.status_code != 200) {
        toast({
          title: response?.data?.message,
          status: 'error',
          duration: 1000,
          isClosable: true,
          position: 'top',
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  if (!permissions?.read) {
    return <NoPermissionsMessage module={Modules.PermissionGroup} />;
  }

  return fetchListError ? (
    <PageError />
  ) : (
    <Flex
      height='100%'
      mt='8rem'
      direction='column'>
      <Flex
        direction={{ sm: 'column', md: 'row' }}
        px={5}
        mb='20px'
        width='100%'>
        <Flex
          direction={{ sm: 'column', md: 'row' }}
          gap={10}>
          <Input
            variant='auth'
            type='text'
            placeholder='Search Group..'
            onChange={(e) => handleInputChange(e.target.value)} // Use debounced handler
          />
        </Flex>

        {permissions?.read && permissions?.create && (
          <Flex ml='auto'>
            <Chakralink>
              <Link
                to={{
                  pathname: `/admin/addpermissiongroup`,
                }}
                onClick={resetSelectedPermissions}>
                <Button
                  variant='lightBrand'
                  fontSize='sm'
                  fontWeight='500'
                  borderRadius='10px'
                  boxShadow='lg'
                  px='15px'
                  py='5px'>
                  <AddIcon />
                </Button>
              </Link>
            </Chakralink>
          </Flex>
        )}
      </Flex>

      <TableWrapper
        list={list}
        loading={loading}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        listTotal={listTotal}
        itemsPerPage={itemsPerPage}
        isError={isError}>
        <Table
          variant='striped'
          color='gray.500'
          mb='24px'>
          <Thead>
            <Tr>
              <Th
                pe='10px'
                borderColor={borderColor}>
                <Flex
                  justify='space-between'
                  align='center'
                  fontSize={{ sm: '10px', lg: '12px' }}
                  color='gray.400'>
                  Sr.no
                </Flex>
              </Th>
              <Th
                pe='10px'
                borderColor={borderColor}>
                <Flex
                  justify='space-between'
                  align='center'
                  fontSize={{ sm: '10px', lg: '12px' }}
                  color='gray.400'>
                  Name
                </Flex>
              </Th>
              {/* <Th
                  pe='10px'
                  borderColor={borderColor}>
                  <Flex
                    justify='space-between'
                    align='center'
                    fontSize={{ sm: '10px', lg: '12px' }}
                    color='gray.400'>
                    Description
                  </Flex>
                </Th> */}
              <Th
                pe='10px'
                borderColor={borderColor}>
                <Flex
                  justify='space-between'
                  align='center'
                  fontSize={{ sm: '10px', lg: '12px' }}
                  color='gray.400'>
                  Last Updated
                </Flex>
              </Th>
              <Th
                pe='10px'
                borderColor={borderColor}>
                <Flex
                  justify='space-between'
                  align='center'
                  fontSize={{ sm: '10px', lg: '12px' }}
                  color='gray.400'>
                  Status
                </Flex>
              </Th>
              <Th
                pe='10px'
                borderColor={borderColor}>
                <Flex
                  justify='space-between'
                  align='center'
                  fontSize={{ sm: '10px', lg: '12px' }}
                  color='gray.400'>
                  Actions
                </Flex>
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {list?.map((obj, index) => {
              return (
                // <Link to={{ pathname: '/permission-group', state: obj.id }}>
                <Tr key={obj.id}>
                  <Td>
                    <Flex align='center'>
                      <Text
                        me='10px'
                        color={textColor}
                        fontSize='sm'
                        fontWeight='700'>
                        {(currentPage - 1) * 10 + (index + 1)}
                      </Text>
                    </Flex>
                  </Td>
                  <Td>
                    <Flex align='center'>
                      <Text
                        me='10px'
                        color={textColor}
                        fontSize='sm'
                        fontWeight='700'>
                        {obj.name}
                      </Text>
                    </Flex>
                  </Td>
                  {/* <Td>
                      <Flex align='center'>
                        <Text
                          me='10px'
                          color={textColor}
                          fontSize='sm'
                          fontWeight='700'>
                          {obj.description}
                        </Text>
                      </Flex>
                    </Td> */}
                  <Td>
                    <Text
                      me='10px'
                      color={textColor}
                      fontSize='sm'
                      fontWeight='700'>
                      {moment(obj.updated_at).format('DD MMM YY, HH:mm')}
                    </Text>
                  </Td>
                  <Td>
                    <Text
                      size='sm'
                      fontWeight='700'
                      color={obj.is_active === true ? 'green' : { textColor }}>
                      {obj.is_active === true ? 'ACTIVE' : 'INACTIVE'}
                    </Text>
                  </Td>
                  <Td>
                    <Flex justify='space-between'>
                      <Chakralink
                        top='14px'
                        right='14px'
                        mt={{
                          base: '0px',
                          md: '10px',
                          lg: '0px',
                          xl: '10px',
                          '2xl': '0px',
                        }}>
                        <Link
                          to={{
                            pathname: `/admin/permissiongroup/${obj.id}`,
                          }}>
                          <Button>
                            <ViewIcon />
                          </Button>
                        </Link>
                      </Chakralink>
                    </Flex>
                  </Td>
                </Tr>
                // </Link>
              );
            })}
          </Tbody>
        </Table>
      </TableWrapper>
    </Flex>
  );
}
